import { useContext, useEffect, useMemo, useRef, useState } from "react";
import RegistrationLayout from "../../Components/RegistrationLayout/RegistrationLayout";
import Input from "./Input";
import { RegistrationContext } from "../../Context/RegistrationContext";
import ChevronDown from "../../Assets/ChevronDownLargeDarkMode.svg";
import { useOutsideClickForTwoRef } from "../../Hooks/useOutsideClickForTwoRef";
import TickMark from "../../Assets/Modals/TickMark.svg";
import correctIcon from "../../Assets/correct.svg";
import { states } from "./list";
import { showToast } from "../../Components/Toaster/Toaster";
import { fetchPincodeDetails } from "../../lib/utils/apiCalls";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchMasterCategory } from "../../Features/settingsSlice";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

function SectionComponent() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const categoryDropdownButtonRef = useRef(null);
    const categoryDropdownRef = useRef(null);

    const stateDropdownButtonRef = useRef(null);
    const stateDropdownRef = useRef(null);

    const stateWarehouseDropdownButtonRef = useRef(null);
    const stateWarehouseDropdownRef = useRef(null);

    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
    const [stateWarehouseDropdownOpen, setStateWarehouseDropdownOpen] = useState(false);
    const [mapCenterWarehouse, setMapCenterWarehouse] = useState({ lat: 12.957386216133463, lng: 77.6057067083914 });

    const { categories } = useSelector((state) => state.settings.masterCategory);

    const {
        storeDetails,
        setStoreDetails,
        storeCategory,
        setStoreCategory,
        targetAudience,
        setTargetAudience,
        warehouseAddress,
        setWarehouseAddress,
        isWarehouseAddressSame,
        setIsWarehouseAddressSame,
        optionalAddress,
        setOptionalAddress,
        storeErrors,
        setStoreErrors,
        optionalWarehouseAddress,
        setOptionalWarehouseAddress,
        defaultStoreDetailsErrors,

        markerPositionWarehouse,
        setMarkerPositionWarehouse,
    } = useContext(RegistrationContext);

    const options = useMemo(() => {
        if (!Array.isArray(categories)) {
            return [];
        }

        return categories
            ?.filter((category) => category.isActive)
            ?.map((category) => ({
                id: category.keyName,
                label: category.name,
            }))
            .toReversed();
    }, [categories]);

    useOutsideClickForTwoRef(
        categoryDropdownButtonRef,
        categoryDropdownRef,
        () => {
            setCategoryDropdownOpen(false);
        }
    );

    useOutsideClickForTwoRef(stateDropdownButtonRef, stateDropdownRef, () => {
        setStateDropdownOpen(false);
    });

    useOutsideClickForTwoRef(
        stateWarehouseDropdownButtonRef,
        stateWarehouseDropdownRef,
        () => {
            setStateWarehouseDropdownOpen(false);
        }
    );

    function handleChange(e) {
        const { name, value } = e.target;

        if (name === "optionalAddress") {
            setOptionalAddress(value);
        } else if (name === "storePincode") {
            if (value.length > 6) {
                showToast("Max 6 digits allowed", "warning");
                return;
            }
            setStoreDetails((prev) => ({ ...prev, [name]: value }));
        } else {
            setStoreDetails((prev) => ({ ...prev, [name]: value }));
        }
    }

    function handleWarehouseAddressChange(e) {
        const { name, value } = e.target;

        if (name === "optionalWarehouseAddress") {
            setOptionalWarehouseAddress(value);
        } else if (name === "pincode") {
            if (value.length > 6) {
                showToast("Max 6 digits allowed", "warning");
                return;
            }
            setWarehouseAddress((prev) => ({ ...prev, [name]: value }));
        } else {
            setWarehouseAddress((prev) => ({ ...prev, [name]: value }));
        }
    }

    const toggleCategoryDropdown = () => {
        setCategoryDropdownOpen(!categoryDropdownOpen);
    };

    const toggleStateDropdown = () => {
        setStateDropdownOpen(!stateDropdownOpen);
    };

    const toggleStateWarehouseDropdown = () => {
        setStateWarehouseDropdownOpen(!stateWarehouseDropdownOpen);
    };

    const handleCategoryDropdownItemClick = (e, option) => {
        e.stopPropagation();

        let isSelected = storeCategory.some(
            (selected) => selected.id === option.id
        );

        if (isSelected) {
            let filteredList = storeCategory.filter((item) => item.id !== option.id);
            setStoreCategory(filteredList);
        } else {
            setStoreCategory([...storeCategory, option]);
        }
    };

    const handleStateDropdownItemClick = (e, state) => {
        e.stopPropagation();

        let isSelected = state.name === storeDetails.storeState;

        if (isSelected) {
            setStoreDetails({
                ...storeDetails,
                storeState: "",
            });
        } else {
            setStoreDetails({
                ...storeDetails,
                storeState: state.name,
            });
        }
    };

    const handleStateWarehouseDropdownItemClick = (e, state) => {
        e.stopPropagation();

        let isSelected = state.name === warehouseAddress.state;

        if (isSelected) {
            setWarehouseAddress({
                ...warehouseAddress,
                state: "",
            });
        } else {
            setWarehouseAddress({
                ...warehouseAddress,
                state: state.name,
            });
        }
    };

    const toggleTargetAudience = (value) => {
        if (targetAudience.includes(value)) {
            setTargetAudience(targetAudience.filter((item) => item !== value));
        } else {
            setTargetAudience([...targetAudience, value]);
        }
    };

    const handleWarehouseMapClick = (e) => {
        if (!e?.latLng) return;
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setMarkerPositionWarehouse({ lat, lng });
        setMapCenterWarehouse({ lat, lng });
    };

    const mapContainerStyle = {
        width: "100%",
        height: "300px",
        borderRadius: "9px",
    };

    const mapOptions = {
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
    };

    useEffect(() => {
        if (isWarehouseAddressSame) {
            const combinedAddress = optionalAddress 
                ? `${storeDetails.storeAddress}, ${optionalAddress}` 
                : storeDetails.storeAddress;
            
            setWarehouseAddress({
                pincode: storeDetails.storePincode,
                Addr: combinedAddress,
                city: storeDetails.storeCity,
                state: storeDetails.storeState,
            });
        }
        // eslint-disable-next-line
    }, [isWarehouseAddressSame]);

    async function getPincodeDetails(pin) {
        try {
            const response = await fetchPincodeDetails(pin);
            if (response.status === 200) {
                return response.data[0].PostOffice[0];
            }
        } catch (err) {
            showToast(err, "error");
        }
    }

    useEffect(() => {
        if (storeDetails.storePincode.length === 6) {
            (async () => {
                const data = await getPincodeDetails(storeDetails.storePincode);
                if (data) {
                    setStoreDetails(prev => ({
                        ...prev,
                        storeState: data.State,
                        storeCity: data.Region,
                    }));

                    if (isWarehouseAddressSame) {
                        setWarehouseAddress(prev => ({
                            ...prev,
                            state: data.State,
                            city: data.Region,
                            pincode: storeDetails.storePincode,
                        }));
                    }
                }
            })();
        }
        // eslint-disable-next-line
    }, [storeDetails.storePincode]);

    useEffect(() => {
        if (warehouseAddress.pincode.length === 6 && !isWarehouseAddressSame) {
            (async () => {
                const data = await getPincodeDetails(warehouseAddress.pincode);
                if (data) {
                    setWarehouseAddress(prev => ({
                        ...prev,
                        state: data.State,
                        city: data.Region,
                    }));
                }
            })();
        }
        // eslint-disable-next-line
    }, [warehouseAddress.pincode, isWarehouseAddressSame]);

    function handleNext() {
        const validationErrors = {};

        if (!storeDetails.storeName) {
            validationErrors.storeName = "Required field";
        }

        if (storeCategory.length === 0) {
            validationErrors.storeCategory = "Select category";
        }

        if (targetAudience.length === 0) {
            validationErrors.targetAudience = "Select target audience";
        }

        if (storeDetails.description.length > 250) {
            validationErrors.description = "Max 250 chars";
        }

        if (!storeDetails.storePincode) {
            validationErrors.storePincode = "Required field";
        }

        if (!storeDetails.storeAddress) {
            validationErrors.storeAddress = "Required field";
        }

        if (!storeDetails.storeCity) {
            validationErrors.storeCity = "Required field";
        }

        if (!storeDetails.storeState) {
            validationErrors.storeState = "Required field";
        }

        if(!markerPositionWarehouse) {
            validationErrors.warehouseMarker = "Please select the coordinates";
        }

        if (!isWarehouseAddressSame) {
            if (!warehouseAddress.pincode) {
                validationErrors.pincode = "Required field";
            }

            if (!warehouseAddress.Addr) {
                validationErrors.Addr = "Required field";
            }

            if (!warehouseAddress.city) {
                validationErrors.city = "Required field";
            }

            if (!warehouseAddress.state) {
                validationErrors.state = "Required field";
            }
        }

        if (Object.keys(validationErrors).length === 0) {
            setStoreErrors(defaultStoreDetailsErrors);
            navigate("/bankdetails");
            return;
        } else {
            setStoreErrors(validationErrors);
        }
    }

    useEffect(() => {
        dispatch(fetchMasterCategory());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GMAPS_API}>
            <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4">
                        <h1 className="font-semibold text-[24px] leading-[44px] text-text1">
                            Business Information
                        </h1>

                        <div className="flex flex-col gap-3">
                            <Input
                                labelText="Store Name"
                                errorText={storeErrors.storeName}
                                inputOptions={{
                                    type: "text",
                                    id: "storeName",
                                    name: "storeName",
                                    value: storeDetails.storeName,
                                    onChange: handleChange,
                                    placeholder: "enter store name",
                                }}
                            />

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <h1 className="select-none cursor-pointer w-fit font-medium text-base text-[#121212]">
                                        Select Category
                                    </h1>

                                    {storeErrors.storeCategory && (
                                        <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                            {storeErrors.storeCategory}
                                        </p>
                                    )}
                                </div>

                                <div
                                    ref={categoryDropdownButtonRef}
                                    onClick={toggleCategoryDropdown}
                                    className="relative cursor-pointer px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] flex items-center gap-[10px]"
                                >
                                    <p className="w-[calc(100%-24px)] text-base text-text2 line-clamp-1">
                                        {storeCategory.length > 0
                                            ? storeCategory.map((item) => item.label).join(", ")
                                            : "select category"}
                                    </p>

                                    <div className="size-6 flex items-center justify-center">
                                        <img src={ChevronDown} alt="" />
                                    </div>

                                    {categoryDropdownOpen && (
                                        <div
                                            ref={categoryDropdownRef}
                                            className="absolute bg-[#FFFFFF] max-h-56 overflow-y-scroll z-10 top-14 left-0 w-full border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0px_0px_12px_0px_#0000003D]"
                                        >
                                            {options.map((option) => {
                                                let isSelected = storeCategory.some(
                                                    (selected) => selected.id === option.id
                                                );

                                                return (
                                                    <div
                                                        key={option.id}
                                                        onClick={(e) =>
                                                            handleCategoryDropdownItemClick(e, option)
                                                        }
                                                        className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5]"
                                                    >
                                                        <div
                                                            className={`w-6 h-6 cursor-pointer rounded-md ${
                                                                isSelected
                                                                    ? "border-none bg-primaryColor flex items-center justify-center"
                                                                    : "border border-[#D1D5DB]"
                                                            } `}
                                                        >
                                                            <img
                                                                src={isSelected ? TickMark : undefined}
                                                                alt=""
                                                            />
                                                        </div>

                                                        <p className="font-medium select-none text-base text-[#666666]">
                                                            {option.label}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <h1 className="select-none cursor-pointer w-fit font-medium text-base text-text1">
                                        Target Audience
                                    </h1>

                                    {storeErrors.targetAudience && (
                                        <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                            {storeErrors.targetAudience}
                                        </p>
                                    )}
                                </div>

                                <input
                                    type="checkbox"
                                    id="targetAudienceMen"
                                    name="targetAudience"
                                    checked={targetAudience.includes("MEN")}
                                    onChange={() => toggleTargetAudience("MEN")}
                                    className="hidden"
                                />

                                <input
                                    type="checkbox"
                                    id="targetAudienceWomen"
                                    name="targetAudience"
                                    checked={targetAudience.includes("WOMEN")}
                                    onChange={() => toggleTargetAudience("WOMEN")}
                                    className="hidden"
                                />

                                <input
                                    type="checkbox"
                                    id="targetAudienceKids"
                                    name="targetAudience"
                                    checked={targetAudience.includes("KIDS")}
                                    onChange={() => toggleTargetAudience("KIDS")}
                                    className="hidden"
                                />

                                <div className="flex items-center gap-3">
                                    <label
                                        htmlFor="targetAudienceMen"
                                        className="cursor-pointer font-normal text-[14px] leading-6 text-text1 flex items-center gap-2"
                                    >
                                        <div
                                            className={`size-6 ${
                                                targetAudience.includes("MEN")
                                                    ? "bg-primaryColor"
                                                    : "bg-transparent"
                                            } flex items-center justify-center rounded-md border border-[#D1D5DB]`}
                                        >
                                            {targetAudience.includes("MEN") && (
                                                <img src={correctIcon} alt="" />
                                            )}
                                        </div>
                                        Men
                                    </label>

                                    <label
                                        htmlFor="targetAudienceWomen"
                                        className="cursor-pointer font-normal text-[14px] leading-6 text-text1 flex items-center gap-2"
                                    >
                                        <div
                                            className={`size-6 ${
                                                targetAudience.includes("WOMEN")
                                                    ? "bg-primaryColor"
                                                    : "bg-transparent"
                                            } flex items-center justify-center rounded-md border border-[#D1D5DB]`}
                                        >
                                            {targetAudience.includes("WOMEN") && (
                                                <img src={correctIcon} alt="" />
                                            )}
                                        </div>
                                        Women
                                    </label>

                                    <label
                                        htmlFor="targetAudienceKids"
                                        className="cursor-pointer font-normal text-[14px] leading-6 text-text1 flex items-center gap-2"
                                    >
                                        <div
                                            className={`size-6 ${
                                                targetAudience.includes("KIDS")
                                                    ? "bg-primaryColor"
                                                    : "bg-transparent"
                                            } flex items-center justify-center rounded-md border border-[#D1D5DB]`}
                                        >
                                            {targetAudience.includes("KIDS") && (
                                                <img src={correctIcon} alt="" />
                                            )}
                                        </div>
                                        Kids
                                    </label>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <label
                                        htmlFor="description"
                                        className="cursor-pointer text-base text-text1 font-medium"
                                    >
                                        Store Description &#40;optional&#41; &#40;
                                        {storeDetails.description.length}
                                        {"/"}250&#41;
                                    </label>

                                    {storeErrors.description && (
                                        <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                            {storeErrors.description}
                                        </p>
                                    )}
                                </div>

                                <textarea
                                    name="description"
                                    id="description"
                                    value={storeDetails.description}
                                    onChange={handleChange}
                                    placeholder="Write a short description about your brand, product quality..."
                                    className="outline-none px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] placeholder:text-text2 text-text1 font-normal text-base focus:border-primaryColor"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4">
                        <h1 className="font-semibold text-[24px] leading-[44px] text-text1">
                            Enter Store Address
                        </h1>

                        <div className="flex flex-col gap-3">
                            <Input
                                labelText="Pincode"
                                errorText={storeErrors.storePincode}
                                inputOptions={{
                                    type: "number",
                                    id: "storePincode",
                                    name: "storePincode",
                                    value: storeDetails.storePincode,
                                    onChange: handleChange,
                                    placeholder: "Eg: 110011",
                                }}
                            />

                            <Input
                                labelText="Adress Line 1"
                                errorText={storeErrors.storeAddress}
                                inputOptions={{
                                    type: "text",
                                    id: "storeAddress",
                                    name: "storeAddress",
                                    value: storeDetails.storeAddress,
                                    onChange: handleChange,
                                    placeholder: "door no / building name / Floor no",
                                }}
                            />

                            <Input
                                labelText="Adress Line 2 (optional)"
                                // errorText={storeErrors.optionalAddress} 
                                inputOptions={{
                                    type: "text",
                                    id: "optionalAddress",
                                    name: "optionalAddress",
                                    value: optionalAddress,
                                    onChange: handleChange,
                                    placeholder: "Landmark",
                                }}
                            />

                            <Input
                                labelText="City"
                                errorText={storeErrors.storeCity}
                                inputOptions={{
                                    type: "text",
                                    id: "storeCity",
                                    name: "storeCity",
                                    value: storeDetails.storeCity,
                                    onChange: handleChange,
                                    placeholder: "City",
                                }}
                            />

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <h1 className="select-none cursor-pointer w-fit font-medium text-base text-text1">
                                        State
                                    </h1>

                                    {storeErrors.storeState && (
                                        <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                            {storeErrors.storeState}
                                        </p>
                                    )}
                                </div>

                                <div
                                    ref={stateDropdownButtonRef}
                                    onClick={toggleStateDropdown}
                                    className="relative cursor-pointer px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] flex items-center gap-[10px]"
                                >
                                    <p className="w-[calc(100%-24px)] text-base text-text2 line-clamp-1">
                                        {storeDetails.storeState
                                            ? storeDetails.storeState
                                            : "Select state"}
                                    </p>

                                    <div className="size-6 flex items-center justify-center">
                                        <img src={ChevronDown} alt="" />
                                    </div>

                                    {stateDropdownOpen && (
                                        <div
                                            ref={stateDropdownRef}
                                            className={`absolute bg-[#FFFFFF] ${
                                                isWarehouseAddressSame ? "max-h-44" : "max-h-56"
                                            } overflow-y-scroll z-10 top-14 left-0 w-full border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0px_0px_12px_0px_#0000003D]`}
                                        >
                                            {states.map((state) => {
                                                let isSelected = state.name === storeDetails.storeState;

                                                return (
                                                    <div
                                                        key={state.id}
                                                        onClick={(e) =>
                                                            handleStateDropdownItemClick(e, state)
                                                        }
                                                        className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5]"
                                                    >
                                                        <div
                                                            className={`w-6 h-6 cursor-pointer rounded-md ${
                                                                isSelected
                                                                    ? "border-none bg-primaryColor flex items-center justify-center"
                                                                    : "border border-[#D1D5DB]"
                                                            } `}
                                                        >
                                                            <img
                                                                src={isSelected ? TickMark : undefined}
                                                                alt=""
                                                            />
                                                        </div>

                                                        <p className="font-medium select-none text-base text-[#666666]">
                                                            {state.name}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <h1 className="select-none cursor-pointer w-fit font-medium text-base text-text2">
                                    Country
                                </h1>

                                <div className="px-3 py-4 rounded-[9px] bg-[#EAEAEA] border border-[#EAEAEA]">
                                    <p className="text-base text-[#999999] font-normal">India</p>
                                </div>
                            </div>
                        </div>

                        <div className="select-none">
                            <input
                                type="checkbox"
                                id="same-email"
                                name="same-email"
                                checked={isWarehouseAddressSame}
                                onChange={() =>
                                    setIsWarehouseAddressSame(!isWarehouseAddressSame)
                                }
                                className="hidden"
                            />

                            <label
                                htmlFor="same-email"
                                className="cursor-pointer font-normal text-[14px] leading-6 text-text1 flex items-center gap-2"
                            >
                                <div
                                    className={`size-6 ${
                                        isWarehouseAddressSame ? "bg-primaryColor" : "bg-transparent"
                                    } flex items-center justify-center rounded-md border border-[#D1D5DB]`}
                                >
                                    {isWarehouseAddressSame && <img src={correctIcon} alt="" />}
                                </div>
                                Use same address for warehouse
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4">
                        <h1 className="font-semibold text-[24px] leading-[44px] text-text1">
                            Enter Warehouse Address
                        </h1>

                        <div className="flex flex-col">
                            <div className="flex flex-col gap-2">
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={mapCenterWarehouse}
                                    zoom={15}
                                    onClick={handleWarehouseMapClick}
                                    options={mapOptions}
                                >
                                    {markerPositionWarehouse && <Marker position={markerPositionWarehouse} />}
                                </GoogleMap>

                                {
                                    storeErrors.warehouseMarker && 
                                    <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                        {storeErrors.warehouseMarker}
                                    </p>
                                }
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <Input
                                labelText="Pincode"
                                errorText={storeErrors.pincode}
                                inputOptions={{
                                    type: "number",
                                    id: "pincode",
                                    name: "pincode",
                                    value: warehouseAddress.pincode,
                                    onChange: handleWarehouseAddressChange,
                                    placeholder: "Eg: 110011",
                                }}
                            />

                            <Input
                                labelText="Adress Line 1"
                                errorText={storeErrors.Addr}
                                inputOptions={{
                                    type: "text",
                                    id: "Addr",
                                    name: "Addr",
                                    value: warehouseAddress.Addr,
                                    onChange: handleWarehouseAddressChange,
                                    placeholder: "door no / building name / Floor no",
                                }}
                            />

                            <Input
                                labelText="Adress Line 2 (optional)"
                                errorText={storeErrors.optionalWarehouseAddress}
                                inputOptions={{
                                    type: "text",
                                    id: "optionalWarehouseAddress",
                                    name: "optionalWarehouseAddress",
                                    value: optionalWarehouseAddress,
                                    onChange: handleWarehouseAddressChange,
                                    placeholder: "Landmark",
                                }}
                            />

                            <Input
                                labelText="City"
                                errorText={storeErrors.city}
                                inputOptions={{
                                    type: "text",
                                    id: "city",
                                    name: "city",
                                    value: warehouseAddress.city,
                                    onChange: handleWarehouseAddressChange,
                                    placeholder: "City",
                                }}
                            />

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <h1 className="select-none cursor-pointer w-fit font-medium text-base text-text1">
                                        State
                                    </h1>

                                    {storeErrors.state && (
                                        <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                            {storeErrors.state}
                                        </p>
                                    )}
                                </div>

                                <div
                                    ref={stateWarehouseDropdownButtonRef}
                                    onClick={toggleStateWarehouseDropdown}
                                    className="relative cursor-pointer px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] flex items-center gap-[10px]"
                                >
                                    <p className="w-[calc(100%-24px)] text-base text-text2 line-clamp-1">
                                        {warehouseAddress.state
                                            ? warehouseAddress.state
                                            : "Select state"}
                                    </p>

                                    <div className="size-6 flex items-center justify-center">
                                        <img src={ChevronDown} alt="" />
                                    </div>

                                    {stateWarehouseDropdownOpen && (
                                        <div
                                            ref={stateWarehouseDropdownRef}
                                            className="absolute bg-[#FFFFFF] max-h-32 overflow-y-scroll z-10 top-14 left-0 w-full border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0px_0px_12px_0px_#0000003D]"
                                        >
                                            {states.map((state) => {
                                                let isSelected = state.name === warehouseAddress.state;

                                                return (
                                                    <div
                                                        key={state.id}
                                                        onClick={(e) =>
                                                            handleStateWarehouseDropdownItemClick(e, state)
                                                        }
                                                        className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5]"
                                                    >
                                                        <div
                                                            className={`w-6 h-6 cursor-pointer rounded-md ${
                                                                isSelected
                                                                    ? "border-none bg-primaryColor flex items-center justify-center"
                                                                    : "border border-[#D1D5DB]"
                                                            } `}
                                                        >
                                                            <img
                                                                src={isSelected ? TickMark : undefined}
                                                                alt=""
                                                            />
                                                        </div>

                                                        <p className="font-medium select-none text-base text-[#666666]">
                                                            {state.name}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <h1 className="select-none cursor-pointer w-fit font-medium text-base text-text2">
                                    Country
                                </h1>

                                <div className="px-3 py-4 rounded-[9px] bg-[#EAEAEA] border border-[#EAEAEA]">
                                    <p className="text-base text-[#999999] font-normal">India</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleNext}
                    className="w-full bg-primaryColor hover:bg-primaryAccent text-white px-3 py-4 rounded-[9px] font-semibold text-[18px] leading-6"
                >
                    Next
                </button>
            </div>
        </LoadScript>        
    );
}

function StoreSetup() {
    return (
        <RegistrationLayout>
            <SectionComponent></SectionComponent>
        </RegistrationLayout>
    );
}

export default StoreSetup;
